<template>
  <v-card style="background-color: #f4f3f3" height="100vh">
    <div
      class="pa-10 uk-container uk-padding"
      style="overflow-y: auto; overflow-x: none; height: calc(100vh - 64px)"
    >
      <div class="uk-timeline">
        <Alert
          v-if="hasInvalidCodes"
          dense
          color="error"
          class="mb-4 mx-auto"
          :actionText="''"
        >
          {{ $t("migrationValidation.alertMigrationValidation") }}
        </Alert>

        <Alert
          v-if="hasOnlyWarnings"
          dense
          color="warning"
          class="mb-4 mx-auto"
          :actionText="''"
        >
          {{ $t("migrationValidation.alertWarningMigrationValidation") }}
        </Alert>
        <MigrationAlertUpgradePlan />

        <MigrationsValidationTable :migrations="currentImportedCsvValidation" />
      </div>

      <v-footer
        style="height: 70px; margin: auto"
        color="transparent"
        class="border-bottom rounded-xl mt-8"
        max-width="95vw"
      >
        <v-btn
          text
          height="52"
          color="secondary"
          style="border: 2px solid grey !important"
          class="mx-auto text-none text-body-1 font-weight-medium px-8"
          outlined
          @click="$emit('back')"
          :disabled="loading"
        >
          <v-icon right class="mr-4" size="22">ph-arrow-left</v-icon>
          {{ $t("common.back") }}
        </v-btn>

        <v-spacer />

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                right
                height="52"
                color="accent"
                class="mx-auto text-none text-body-1 font-weight-bold px-8 mr-0"
                @click="openConfirmDialog"
                :disabled="
                  hasInvalidCodes ||
                  loading ||
                  currentImportedCsvValidation.length == 0
                "
                :loading="loading"
              >
                {{ $t("common.advance") }}
                <v-icon right class="ml-4" size="22">ph-arrow-right</v-icon>
              </v-btn>
            </span>
          </template>
          <span v-if="hasInvalidCodes && !loading">
            {{ $t("migrationTooltipMessage.confirmButtonTooltipWithErrors") }}
          </span>
          <span v-else>
            {{
              $t("migrationTooltipMessage.confirmButtonTooltipWithoutErrors")
            }}
          </span>
        </v-tooltip>
      </v-footer>
    </div>

    <SimpleDialog
      :show="showConfirmDialog"
      :title="$t('confirmMigrationModal.confirmMigrationTitle')"
      :customButtom="true"
      outlined
      dense
      showCancel
      :actionText="$t('confirmMigrationModal.confirmMigrationButton')"
      :showLoading="loading"
      :disabledAction="loading"
      :closeText="$t('confirmMigrationModal.cancelMigrationButton')"
      @action="confirmMigrations"
      @close="resetDataValue"
      width="700"
    >
      <template v-slot:body>
        <p class="mt-2 text-title-1">
          {{ $t("confirmMigrationModal.informationConfirmMigration") }}
        </p>

        <Alert :prominent="false" color="warning">
          {{ $t("confirmMigrationModal.migrationUserDeletionWarning") }}
        </Alert>
      </template>
    </SimpleDialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import MigrationAlertUpgradePlan from "./MigrationAlertUpgradePlan.vue";
import MigrationsValidationTable from "./MigrationsValidationTable.vue";

export default {
  name: "MigrationsValidation",

  components: {
    MigrationsValidationTable,
    MigrationAlertUpgradePlan,
  },
  props: {
    migrationsConfig: { type: Array, required: false },
  },
  data() {
    return {
      loading: false,
      showConfirmDialog: false,
    };
  },
  computed: {
    ...mapGetters([
      "token",
      "currentImportedCsvValidation",
      "currentCreateMigrationStep",
    ]),

    allowedCodes() {
      return [
        null,
        "ADD_TO_GROUP",
        "NEED_CREATE",
        "NEW_DRIVE",
        "DEFAULT_VALUE",
        "TRIAL_LIMIT_EXCEEDED",
        "TRIAL_PERIOD_LIMIT",
      ];
    },

    warningCodes() {
      return this.allowedCodes.filter((code) => code !== null);
    },

    hasInvalidCodes() {
      const result = this.currentImportedCsvValidation.some((migration) => {
        const codes = this.extractAllCodes(migration);
        return codes.some((code) => {
          const isInvalid =
            code && code.trim() !== "" && !this.allowedCodes.includes(code);

          return isInvalid;
        });
      });

      return result;
    },

    hasOnlyWarnings() {
      const allCodes = this.currentImportedCsvValidation.flatMap((migration) =>
        this.extractAllCodes(migration)
      );

      if (allCodes.length === 0) return false;

      const hasWarning = allCodes.some((code) =>
        this.warningCodes.includes(code)
      );

      return hasWarning && !this.hasInvalidCodes;
    },
  },

  methods: {
    ...mapMutations(["setSnackBar"]),

    ...mapActions(["batchtMigrations"]),

    extractAllCodes(migration) {
      return [
        migration?.source_email?.code,
        migration?.target_email?.code,
        migration?.target_drive_email?.code,
        migration?.period?.code,
        ...(migration?.shared_with || []).map((item) => item?.code),
      ].filter((code) => code && code.trim() !== "");
    },

    openConfirmDialog() {
      this.showConfirmDialog = true;
    },

    resetDataValue() {
      this.showConfirmDialog = false;
    },

    async confirmMigrations() {
      this.loading = true;

      await this.batchtMigrations(this.currentImportedCsvValidation)
        .then(() => {
          this.resetDataValue();
          this.$emit("close");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style>
.v-tab {
  text-transform: none;
}

.fixed-element {
  position: sticky;
  top: 0;
  z-index: 4;
}

.uk-timeline .uk-timeline-item {
  display: flex;
  position: relative;
}

.uk-timeline .uk-timeline-item::before {
  background: #dadee4;
  content: "";
  height: 100%;
  left: 12px;
  position: absolute;
  top: 24px;
  width: 1px;
}

.uk-timeline .uk-timeline-item:last-of-type::before {
  background: #dadee4;
  content: "";
  height: 100%;
  left: 12px;
  top: 24px;
  width: 0px;
}

.uk-badge {
  background: var(--v-primary-base);
  border-radius: 50%;
  padding: 3px 8px;
  font-size: 0.9rem;
  color: #fff;
  font-weight: bold;
}

.uk-timeline .uk-timeline-item .uk-timeline-icon .uk-badge {
  margin-top: 20px;
  width: 40px;
  height: 40px;
  font-family: "Roboto";
}

.uk-timeline .uk-timeline-item .uk-timeline-content {
  -ms-flex: 1 1 auto;
  flex: 1 1 auto;
  padding: 0 0 0 1rem;
  font-family: "Roboto";
}
</style>
